/**
 * The assistant's interview, as one card.
 *
 * One message, patched in place — not one message per question. Every message
 * is JSON-stringified into localStorage and `content` is what the sidebar
 * search, the thread title, the Markdown export and the model's own history
 * read; seven interview turns would pollute all four with partial transcripts.
 *
 * The stages map straight onto `datasetInterview.js`:
 *   ask      one question, with chips, and every prior answer as an editable line
 *   work     the expensive step, with a Stop that keeps what it paid for
 *   review   what came out, free to redo, BEFORE anything is created
 *   confirm  the single irreversible moment, fully enumerated
 *   running  the steps
 *   done     where to go next
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import { formatBytes } from '../utils/datasetScan';
import { answeredSummary, currentQuestion } from '../utils/datasetInterview';
import BuildStepList from './BuildStepList';
import CardBusyStrip from './CardBusyStrip';
import { STEPS } from '../utils/datasetPlanRunner';

const TYPE_LABEL_KEYS = {
  classification: 'assistant.modelType.classification',
  bounding_box: 'assistant.modelType.detection',
};

const InterviewCard = ({
  message,
  run,
  isHeld,
  onAnswer,
  onReopen,
  onConfirm,
  onCancel,
  onRetryVocabulary,
  onOpenStudio,
  onQueueTraining,
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const state = message.interview;
  const [projectName, setProjectName] = useState(null);
  const [modelName, setModelName] = useState(null);

  if (!state) return null;

  const question = currentQuestion(state);
  const summary = answeredSummary(state);
  const { facts, answers } = state;
  const held = isHeld ? isHeld(message.attachmentId) : false;
  const result = state.result || {};

  /*
   * One busy flag for the whole card.
   *
   * `work` and `running` are both "the machine is doing something you cannot
   * hurry"; the card looked identical to an idle one in both, which is what
   * sent the user to the Change button. Everything that must not be touched
   * mid-flight keys off this, not off a per-stage check.
   */
  const busy = state.stage === 'work' || state.stage === 'running';
  const detection = answers.task === 'detection';
  const percent = run && typeof run.percent === 'number' ? run.percent : null;

  /** The current step of an import, mapped to a label we actually have. */
  const currentStep = run && STEPS.includes(run.current) ? run.current : null;

  const busyLabel = () => {
    if (state.stage === 'work') {
      return detection
        ? t('assistant.interview.status.boxing', {
            names: (answers.objects || []).join(', '),
          })
        : t('assistant.interview.status.grouping', { count: facts.importable });
    }
    return currentStep
      ? t('assistant.interview.status.step', { step: t(`assistant.build.step.${currentStep}`) })
      : t('assistant.interview.status.starting');
  };

  const busyDetail = () => {
    if (state.stage === 'work' && detection) {
      return t('assistant.interview.working.autobox', {
        names: (answers.objects || []).join(', '),
        done: (run && run.done) || 0,
        total: (run && run.total) || facts.importable,
      });
    }
    if (state.stage === 'running' && currentStep === 'verify' && run && run.counted != null) {
      return t('assistant.interview.status.verifying', {
        done: run.counted,
        total: facts.importable,
      });
    }
    return null;
  };

  // Auto-boxing has an exact count, so a real percentage exists even though the
  // run slice reports it as done/total rather than a percent.
  const workPercent =
    state.stage === 'work' && detection && run && run.total
      ? Math.round((run.done / run.total) * 100)
      : percent;

  const plannedProject = projectName != null ? projectName : answers.projectName || facts.baseName;
  const plannedModel =
    modelName != null
      ? modelName
      : `${facts.baseName} ${answers.task === 'detection' ? 'detector' : 'classifier'}`;

  const existing = (state.projects || []).find(
    (project) => (project.name || '').toLowerCase() === String(plannedProject).toLowerCase(),
  );

  return (
    <article
      className={`lf-card${busy ? ' lf-card--busy' : ''}`}
      aria-label={t('assistant.interview.title')}
      aria-busy={busy ? 'true' : 'false'}
    >
      <header className="lf-card__head">
        {/* The badge is the fastest read on the card: it says idle / working /
            finished before any text is parsed. */}
        <span className="lf-card__badge" aria-hidden="true">
          <i
            className={`fa ${
              busy
                ? 'fa-circle-notch fa-spin'
                : state.stage === 'done'
                  ? 'fa-check-circle'
                  : 'fa-comments'
            }`}
          />
        </span>
        <span className="lf-card__heading">
          <strong>{t('assistant.interview.title')}</strong>
          <span className="lf-card__sub">
            {t('assistant.interview.facts', {
              images: facts.totalImages,
              size: formatBytes(facts.importBytes),
            })}
          </span>
        </span>
      </header>

      {/* Everything already decided, and where it came from. */}
      {!!summary.length && (
        <ul className="lf-answers">
          {summary.map((row) => (
            <li key={row.id}>
              <span className="lf-answers__label">{t(row.labelKey)}</span>
              <span className="lf-answers__value">
                {row.value || t(row.valueKey, row.params || {})}
              </span>
              {row.locked ? (
                <em className="lf-answers__from">{t('assistant.interview.summary.fromArchive')}</em>
              ) : (
                // Disabled, not hidden: a control that vanishes mid-run reads
                // as the card losing state. Disabled says "not now".
                state.stage !== 'done' && (
                  <button
                    type="button"
                    className="lf-answers__edit"
                    disabled={busy}
                    title={busy ? t('assistant.interview.status.busyHint') : undefined}
                    aria-label={`${t('assistant.interview.change')}: ${t(row.labelKey)}`}
                    onClick={() => onReopen?.(message, row.id)}
                  >
                    {t('assistant.interview.change')}
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {/*
        The card's error, at CARD level.
        It used to render only inside the `ask` block, so anything that failed
        at another stage — a queue-training request, for one — wrote a message
        into state that nothing on screen ever showed.
      */}
      {state.error && state.stage !== 'ask' && (
        <p className="lf-card__error" role="alert">{state.error}</p>
      )}

      {facts.droppedForCount > 0 && (
        <p className="lf-card__hint">
          {t('assistant.interview.droppedCount', { count: facts.droppedForCount })}
        </p>
      )}
      {facts.droppedForBytes > 0 && (
        <p className="lf-card__hint">
          {t('assistant.interview.droppedBytes', { count: facts.droppedForBytes })}
        </p>
      )}

      {/* ---- ask ---- */}
      {state.stage === 'ask' && question && (
        <div className="lf-ask">
          <p className="lf-ask__prompt">{t(question.promptKey)}</p>
          {question.costKey && (
            <p className="lf-ask__cost">
              <i className="fa fa-bolt" aria-hidden="true" /> {t(question.costKey, question.costParams)}
            </p>
          )}
          {question.kind === 'choice' ? (
            <div className="lf-ask__options">
              {question.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="lf-ask__option"
                  onClick={() => onAnswer?.(message, question.id, option.id)}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          ) : (
            <>
              <p className="lf-card__hint">{t('assistant.interview.ask.objectsHint')}</p>
              {!!(question.suggestions || []).length && (
                <div className="lf-ask__options">
                  {question.suggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="lf-ask__option"
                      onClick={() => onAnswer?.(message, question.id, [name])}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {state.error && <p className="lf-card__error" role="alert">{state.error}</p>}
        </div>
      )}

      {/* ---- work ---- */}
      {state.stage === 'work' && (
        <CardBusyStrip
          label={busyLabel()}
          detail={busyDetail()}
          percent={workPercent}
          phaseKey={`work:${answers.task}`}
          // `message.id`, NOT `message`. cancel() looks the controller up in a
          // Map keyed by id, so passing the object returned undefined and the
          // Stop button silently did nothing at all — for the whole of the
          // longest wait on the card.
          onStop={() => onCancel?.(message.id)}
          stopLabel={
            detection
              ? t('assistant.interview.working.stopKeep')
              : t('assistant.interview.working.stop')
          }
        />
      )}

      {/* ---- review ---- */}
      {state.stage === 'review' && (
        <div className="lf-review">
          <p className="lf-ask__prompt">{t('assistant.interview.review.title')}</p>
          {/*
            The classification headline. It can be stated now because the
            grouping has actually happened by this point — before, this stage
            rendered "Here is what I found" over an EMPTY chip row, because
            planFromInterview names no classes for auto/few/many and the
            clustering ran only after Create was pressed.
          */}
          {!detection && result.grouped != null && (
            <p className="lf-review__coverage">
              {t('assistant.interview.review.grouped', {
                count: result.grouped,
                groups: (result.classes || []).length,
              })}
            </p>
          )}
          {result.cancelled && (
            <p className="lf-card__hint lf-card__hint--reason">
              {t('assistant.interview.review.stopped')}
            </p>
          )}
          {answers.task === 'detection' && (
            <p className={`lf-review__coverage${result.lowCoverage ? ' lf-review__coverage--warn' : ''}`}>
              {t('assistant.interview.review.coverage', {
                found: result.imagesWithBoxes || 0,
                total: result.images || 0,
              })}
            </p>
          )}
          {/* A server failure is not a vocabulary problem, and must not be
              reported as one. */}
          {result.failed > 0 && (
            <p className="lf-card__error" role="alert">
              {t('assistant.interview.review.failedImages', { count: result.failed })}
            </p>
          )}
          {result.lowCoverage && !result.failed && (
            <p className="lf-card__error">
              {t('assistant.interview.review.lowCoverage')}{' '}
              <button type="button" className="lf-card__mini" onClick={() => onRetryVocabulary?.(message)}>
                {t('assistant.interview.review.retry')}
              </button>
            </p>
          )}
          <div className="lf-card__classes">
            {(result.classes || []).map((entry, index) => (
              <span key={entry.id} className="lf-card__chip">
                {entry.name}
                {(result.perClass || [])[index] != null && <em>{result.perClass[index]}</em>}
                {entry.count != null && <em>{entry.count}</em>}
              </span>
            ))}
          </div>
          <button type="button" className="lf-card__run" onClick={() => onAnswer?.(message, 'review', 'ok')}>
            <i className="fa fa-arrow-right" aria-hidden="true" /> {t('common.next')}
          </button>
        </div>
      )}

      {/* ---- confirm: the one irreversible moment ---- */}
      {state.stage === 'confirm' && (
        <div className="lf-confirm">
          <p className="lf-ask__prompt">{t('assistant.interview.confirm.title')}</p>
          <ul className="lf-confirm__lines">
            <li>
              <i className="fa fa-folder-open" aria-hidden="true" />{' '}
              {existing
                ? t('assistant.interview.confirm.existingProject', { project: existing.name })
                : t('assistant.interview.confirm.newProject', { project: plannedProject })}
            </li>
            <li>
              <i className="fa fa-cubes" aria-hidden="true" />{' '}
              {t('assistant.interview.confirm.modelLine', {
                type: t(TYPE_LABEL_KEYS[answers.task === 'detection' ? 'bounding_box' : 'classification']),
                model: plannedModel,
              })}
            </li>
            <li>
              <i className="fa fa-images" aria-hidden="true" />{' '}
              {t('assistant.interview.confirm.imagesLine', { count: facts.importable })}
            </li>
            {!!(result.classes || []).length && (
              <li>
                <i className="fa fa-tags" aria-hidden="true" />{' '}
                {t('assistant.interview.confirm.classesLine', {
                  count: result.classes.length,
                  names: result.classes.map((entry) => entry.name).join(', '),
                })}
              </li>
            )}
          </ul>

          <div className="lf-confirm__names">
            <label>
              {t('assistant.interview.confirm.editProject')}
              <input
                className="lf-card__rename"
                value={plannedProject}
                onChange={(event) => setProjectName(event.target.value)}
              />
            </label>
            <label>
              {t('assistant.interview.confirm.editModel')}
              <input
                className="lf-card__rename"
                value={plannedModel}
                onChange={(event) => setModelName(event.target.value)}
              />
            </label>
          </div>

          <p className="lf-card__hint">{t('assistant.interview.confirm.warnRepeat')}</p>
          {!held && <p className="lf-card__error" role="alert">{t('assistant.attach.expired')}</p>}

          <div className="lf-card__foot">
            <button
              type="button"
              className="lf-card__run"
              disabled={!held}
              onClick={() => onConfirm?.(message, { projectName: plannedProject, modelName: plannedModel })}
            >
              <i className="fa fa-play" aria-hidden="true" />{' '}
              {t('assistant.interview.confirm.go', { count: facts.importable })}
            </button>
          </div>
        </div>
      )}

      {/* ---- running / done ---- */}
      {state.stage === 'running' && (
        <CardBusyStrip
          label={busyLabel()}
          detail={busyDetail()}
          percent={percent}
          // Restarting the counter per step keeps each wait honest: one number
          // climbing past a minute across five steps says nothing useful.
          phaseKey={`run:${currentStep || 'start'}`}
          onStop={() => onCancel?.(message.id)}
          stopLabel={t('assistant.interview.working.stopRun')}
        />
      )}

      {(state.stage === 'running' || state.stage === 'done') && (
        <BuildStepList
          steps={run?.steps}
          percent={run?.percent}
          current={run?.current}
        />
      )}

      {state.stage === 'done' && state.outcome && (
        <>
          {/*
            Three outcomes, not two. The runner returns `failed` for a run that
            created nothing, and this used to render it with the PARTIAL string
            — "imported 0 of 300" — so a total failure read as a nearly-complete
            import, and `outcome.error` was never shown anywhere at all.
          */}
          <p
            className={`lf-card__result${
              state.outcome.status === 'done'
                ? ''
                : state.outcome.status === 'failed'
                  ? ' lf-card__result--bad'
                  : ' lf-card__result--warn'
            }`}
            role={state.outcome.status === 'failed' ? 'alert' : 'status'}
          >
            <i
              className={`fa ${
                state.outcome.status === 'done'
                  ? 'fa-check-circle'
                  : state.outcome.status === 'failed'
                    ? 'fa-times-circle'
                    : 'fa-exclamation-triangle'
              }`}
              aria-hidden="true"
            />{' '}
            {state.outcome.status === 'failed'
              ? t('assistant.interview.finish.failed')
              : t(`assistant.build.result.${state.outcome.status === 'done' ? 'done' : 'partial'}`, {
                  imported: state.outcome.imported,
                  expected: state.outcome.expected,
                  model: state.outcome.modelName,
                })}
          </p>
          {state.outcome.status !== 'done' && state.outcome.error && (
            <p className="lf-card__hint lf-card__hint--reason">{state.outcome.error}</p>
          )}
          <div className="lf-card__foot">
            {state.outcome.modelDoc && (
              <button type="button" className="lf-card__run" onClick={() => onOpenStudio?.(state.outcome, navigate)}>
                <i className="fa fa-tags" aria-hidden="true" /> {t('assistant.interview.finish.openStudio')}
              </button>
            )}
            <button
              type="button"
              className="lf-card__link"
              disabled={!!state.trainBlockedKey || state.trainQueued}
              title={state.trainBlockedKey ? t(state.trainBlockedKey, state.trainBlockedParams || {}) : ''}
              onClick={() => onQueueTraining?.(message)}
            >
              <i className="fa fa-bolt" aria-hidden="true" />{' '}
              {state.trainQueued
                ? t('assistant.interview.finish.queued')
                : t('assistant.interview.finish.queueTraining')}
            </button>
          </div>
          {state.trainBlockedKey && (
            <p className="lf-card__hint">{t(state.trainBlockedKey, state.trainBlockedParams || {})}</p>
          )}
        </>
      )}
    </article>
  );
};

export default InterviewCard;
